import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './OrdersList.css';
import RoomLaunchSidebar from './RoomLaunchSidebar';
import { useNavigate , Link } from 'react-router-dom';
import { API_URL } from '../../config/api';  
import OrderReceipt from '../OrderReceipt';
import OrderWorksheet from '../OrderWorksheet';
import ProductionSchedule from '../WorkOrderProfile/ProductionSchedule';
import { useOrderBalance, formatCurrency } from '../hooks/useOrderBalance';
import EditableCell from '../EditableCell';
import { formatDateTimeForUser, formatPhone, formatShortDate } from '../../utils/dateUtils';
import EmailInvoiceStandalone from './EmailInvoiceStandalone';
import DateRangeReport from './DateRangeReport';
import { getCurrentUser } from '../../utils/auth';
import { restrictedUserList } from '../../utils/userUtils';
import { useLookoutList } from '../../utils/lookoutList';
import { enrichOrderForLookout } from '../../utils/lookoutEnrichment';

function OrdersList({ onPrintWorksheet, onPrintReceipt }) {
  const currentUser = getCurrentUser();
  const { isBookmarked, toggle: toggleLookout } = useLookoutList();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roomsData, setRoomsData] = useState({});
  // Drag-to-scroll refs for table container
  const tableContainerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const scrollLeftStartRef = useRef(0);
  const longPressTimerRef = useRef(null);
  const [isDragMode, setIsDragMode] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(500); // Initial display limit
  const [viewMode, setViewMode] = useState('table');
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [roomTasks, setRoomTasks] = useState({});
  const [loadingTasks, setLoadingTasks] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [warrantyData, setWarrantyData] = useState({});
  const [loadingWarranty, setLoadingWarranty] = useState(new Set());
  const [savingTasks, setSavingTasks] = useState(new Set());
  const [showReceipt, setShowReceipt] = useState(false);
  const [showWorksheet, setShowWorksheet] = useState(false);
  const [currentPrintOrderNo, setCurrentPrintOrderNo] = useState(null);
  const [worksheetSpecificData, setWorksheetSpecificData] = useState(null);
  const [completeOrderData, setCompleteOrderData] = useState(null);
  const [orderNoSearch, setOrderNoSearch] = useState('');
  const [isOrderSearching, setIsOrderSearching] = useState(false);
  const [orderSearchError, setOrderSearchError] = useState(null);
  const [printOrderFinancialData, setPrintOrderFinancialData] = useState({
    lineItems: { items: [], loaded: false },
    payments: { payments: [], loaded: false }
  });

  // Filter states
  const [filters, setFilters] = useState({
    warehouse: '',
    wigType: '',
    enteredBy: '',
    orderStatus: '',
    orderType: '' // Stock or Client
  });

  // EmailInvoice wrapper that fetches data on demand
  const EmailInvoiceButton = ({ orderNo }) => {
    const [orderData, setOrderData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async (e) => {
      e.stopPropagation();
      e.preventDefault();

      if (orderData) {
        // Data already loaded, the standalone component will handle opening the modal
        return;
      }

      setIsLoading(true);
      try {
        const [completeData, lineItems, payments] = await Promise.all([
          fetchCompleteOrderData(orderNo),
          fetchLineItemsForPrint(orderNo),
          fetchPaymentsForPrint(orderNo)
        ]);

        if (!completeData) {
          throw new Error('Failed to fetch order data');
        }

        setOrderData({
          orderDetails: completeData.orderDetails,
          customerInfo: completeData.customerInfo,
          lineItemsData: lineItems,
          paymentsData: payments,
          wigDetails: completeData.wigDetails
        });
      } catch (error) {
        console.error('Error fetching email data:', error);
        alert('Failed to load order data. Please try again.');
        setIsLoading(false);
      }
    };

    // If data is loaded, render the standalone email component
    if (orderData) {
      return (
        <EmailInvoiceStandalone
          orderDetails={orderData.orderDetails}
          customerInfo={orderData.customerInfo}
          lineItemsData={orderData.lineItemsData}
          paymentsData={orderData.paymentsData}
          wigDetails={orderData.wigDetails}
          buttonClassName="btn btn-warning rounded-pill shadow-sm"
          onSuccess={(result) => {
            console.log('✅ Email sent:', result);
            // Reset data so button can be clicked again
            setOrderData(null);
          }}
          onError={(error) => {
            console.error('❌ Email failed:', error);
          }}
        />
      );
    }

    // Otherwise, show a loading button
    return (
      <button
        className="btn btn-warning rounded-pill shadow-sm"
        onClick={handleClick}
        disabled={isLoading}
        title="Email Invoice"
      >
        {isLoading ? (
          <>
            <i className="fas fa-spinner fa-spin mr-1"></i> Loading...
          </>
        ) : (
          <>
            <i className="fas fa-envelope mr-1"></i> Email Invoice
          </>
        )}
      </button>
    );
  };

  // Fetch complete order data including full customer info
  const fetchCompleteOrderData = async (orderNo) => {
  try {    
    const response = await fetch(`${API_URL}/api/orders/order/${orderNo}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data) {
      console.log('✅ Complete order data fetched:', data);
      
      return {
        orderDetails: {
          orderNo: data.orderNo,
          orderDate: data.orderDate,
          dueDate: data.dueDate,
          orderStatus: data.orderStatus,
          orderType: data.orderType,
          customerName: data.customerName,
          customerPhone: data.customerPhone,
          customerEmail: data.customerEmail,
          customerAddress: data.customerAddress,
          customerCity: data.customerCity,
          customerState: data.customerState,
          customerZip: data.customerZip,
          wigBarCode: data.wigBarCode,
          wigSerialNo: data.wigSerialNo,
          orderNotes: data.orderNotes,
          customerNo: data.customerNo,
          orderWarehouse: data.warehouse || data.location, // ✅ Use the correct field from API
          warehouse: data.warehouse, // ✅ Also map directly
          location: data.location
        },
        customerInfo: {
          customerName: data.customerName,
          ID: data.customerNo,
          phone: data.customerPhone,
          email: data.customerEmail,
          address: data.customerAddress,
          city: data.customerCity,
          state: data.customerState,
          zip: data.customerZip,
          orderType: data.orderType
        },
        wigDetails: {
          wigType: data.wigType,
          netSize: data.netSize,
          netType: data.netType,
          baseColor: data.baseColor,
          hairLength: data.wigLength,
          hairType: data.hairType,
          laceType: data.laceType,
          cutStyle: data.cutStyle,
          notes: data.orderNotes
        }
      };
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error fetching complete order data:', error);
    return null;
  }
};


// Initial fetch and refetch when server-side filters change
useEffect(() => {
  const serverFilters = {
    warehouse: filters.warehouse,
    orderStatus: filters.orderStatus,
    orderType: filters.orderType
  };
  fetchOrders(searchTerm, 1, false, serverFilters);
}, [filters.warehouse, filters.orderStatus, filters.orderType]);

  const fetchWorksheetData = async (orderNo) => {
    try {
      const response = await fetch(`${API_URL}/api/orders/worksheet/${orderNo}`);
      const worksheetData = await response.json();
      return worksheetData;
    } catch (error) {
      console.error('Error fetching worksheet data:', error);
      return null;
    }
  };
  
  // Fetch line items for printing
const fetchLineItemsForPrint = async (orderNo) => {
  try {
    const response = await fetch(`${API_URL}/api/orders/${orderNo}/items`);
    const data = await response.json();
    
    if (data.success) {
      return {
        items: data.items || [],
        loaded: true,
        loading: false,
        error: null
      };
    } else {
      throw new Error(data.message || 'Failed to fetch line items');
    }
  } catch (error) {
    console.error('❌ Error fetching line items:', error);
    return {
      items: [],
      loaded: true,
      loading: false,
      error: error.message
    };
  }
};

    // Fetch payments for printing
    const fetchPaymentsForPrint = async (orderNo) => {
      try {
        const response = await fetch(`${API_URL}/api/orders/${orderNo}/payments`);
        const data = await response.json();
        
        if (data.success) {
          return {
            payments: data.payments || [],
            loaded: true,
            loading: false,
            error: null
          };
        } else {
          throw new Error(data.message || 'Failed to fetch payments');
        }
      } catch (error) {
        console.error('❌ Error fetching payments:', error);
        return {
          payments: [],
          loaded: true,
          loading: false,
          error: error.message
        };
      }
    };
  




  const isDueSoon = (dueDate, orderStatus, CustomerFullName) => {
    if (!dueDate) return false;

    // Don't highlight if order is already complete
    if (orderStatus && orderStatus.toLowerCase() === 'completed') {
      return false;
    }  
      
    if (CustomerFullName && CustomerFullName.toLowerCase() === 'stock wig') {
      return false;
    }

    try {
      const due = new Date(dueDate);
      const today = new Date();
      const threeDaysFromNow = new Date();
      threeDaysFromNow.setDate(today.getDate() + 3);

      // Reset time parts to compare only dates
      due.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      threeDaysFromNow.setHours(0, 0, 0, 0);

      // Check if due date is between today and 3 days from now
      return due >= today && due <= threeDaysFromNow;
    } catch (error) {
      return false;
    }
  };
    
    const getCurrentOrderData = () => {
      if (!currentPrintOrderNo) return null;
      
      // Use complete order data if available, otherwise fall back to orders array
      if (completeOrderData) {
        return {
          ...completeOrderData,
          roomTasks: roomTasks[currentPrintOrderNo] || []
        };
      }
      
      // Fallback to orders array data (less complete)
      const order = orders.find(o => (o.orderno || o.OrderNo) === currentPrintOrderNo);
      if (!order) return null;
      
      return {
        orderDetails: {
          orderNo: order.orderno || order.OrderNo,
          orderDate: order.OrderDate,
          customerName: order.OrderCustomerName,
          customerPhone: formatPhone(order.OrderPhone),
          orderType: order.OrderWigType
        },
        customerInfo: {
          customerName: order.OrderCustomerName,
          phone: formatPhone(order.OrderPhone)
        },
        wigDetails: {},
        roomTasks: roomTasks[currentPrintOrderNo] || []
      };
    };



    const handlePrintWorksheet = async (orderNo) => {
      setCurrentPrintOrderNo(orderNo);
      const worksheetData = await fetchWorksheetData(orderNo);
      
      if (worksheetData) {
        setWorksheetSpecificData(worksheetData);
      }    
      setShowWorksheet(true);
      document.body.classList.add('modal-open');
    };

    const handlePrintReceipt = async (orderNo) => {
      setCurrentPrintOrderNo(orderNo);
      
      // Fetch complete order data, financial data, and room tasks
      console.log('🔄 Fetching complete data for receipt...');
      
      const [completeData, lineItemsData, paymentsData] = await Promise.all([
        fetchCompleteOrderData(orderNo),
        fetchLineItemsForPrint(orderNo),
        fetchPaymentsForPrint(orderNo)
      ]);
      
      setCompleteOrderData(completeData);
      setPrintOrderFinancialData({
        lineItems: lineItemsData,
        payments: paymentsData
      });
      
      setShowReceipt(true);
      document.body.classList.add('modal-open');
    };

    const handleCloseReceipt = () => {
      setShowReceipt(false);
      setCurrentPrintOrderNo(null);
      setCompleteOrderData(null);  // ✅ Clear complete order data
      setPrintOrderFinancialData({
        lineItems: { items: [], loaded: false },
        payments: { payments: [], loaded: false }
      });
      document.body.classList.remove('modal-open');
    };

    const handleCloseWorksheet = () => {
      setShowWorksheet(false);
      setCurrentPrintOrderNo(null);
      setWorksheetSpecificData(null);
      document.body.classList.remove('modal-open');
    };

    // Use provided handlers or fall back to internal ones
    const worksheetHandler = onPrintWorksheet || handlePrintWorksheet;
    const receiptHandler = onPrintReceipt || handlePrintReceipt;

  

  const searchSpecificOrder = async (orderNo) => {
  if (!orderNo.trim()) return;
  
  try {
    setIsOrderSearching(true);
    setOrderSearchError(null);
    
    // Use a specific endpoint or add a parameter to search ONLY order numbers
    const response = await fetch(`${API_URL}/api/orders?orderNo=${encodeURIComponent(orderNo)}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    const ordersArray = data.orders ? data.orders : (Array.isArray(data) ? data : []);
    
    // Filter to ensure we only get exact order number matches
    const exactMatches = ordersArray.filter(order => {
      const orderNumber = (order.orderno || order.OrderNo || '').toString();
      return orderNumber === orderNo.trim();
    });
    
    if (exactMatches.length > 0) {
      setOrders(exactMatches);
      const foundOrderNo = exactMatches[0].orderno || exactMatches[0].OrderNo;
      
      // Auto-expand the found order
      setExpandedRows(new Set([foundOrderNo]));
      
      // Fetch room tasks and warranty info for the found order
      // if (!roomTasks[foundOrderNo]) {
      //   fetchRoomTasks(foundOrderNo);
      // }
      if (!warrantyData[foundOrderNo]) {
        fetchWarrantyInfo(foundOrderNo);
      }
    } else {
      setOrderSearchError(`Order #${orderNo} not found`);
      setOrders([]);
    }
    
  } catch (error) {
    console.error('Error searching for order:', error);
    setOrderSearchError(`Error: ${error.message}`);
    setOrders([]);
  } finally {
    setIsOrderSearching(false);
  }
};



  const handleOrderSearchClick = () => {
    searchSpecificOrder(orderNoSearch);
  };
    

  useEffect(() => {
    const handleTransferCompleted = (event) => {
      const { orderNo, newCurrentRoom, transferDetails } = event.detail;
    };
  
    window.addEventListener('wigTransferCompleted', handleTransferCompleted);
    
    return () => {
      window.removeEventListener('wigTransferCompleted', handleTransferCompleted);
    };
  }, [roomTasks, searchTerm]);

  // Sorting state - no default key since backend handles initial sort (ID DESC, OrderDate DESC)
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'desc'
  });

  // Sorting functions
  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });
  };

  // Drag-to-scroll handlers for table container
  const handleTableMouseDown = (e) => {
    // Only trigger on left mouse button
    if (e.button !== 0) return;

    // Start long press timer (300ms to activate drag mode)
    longPressTimerRef.current = setTimeout(() => {
      if (tableContainerRef.current) {
        isDraggingRef.current = true;
        setIsDragMode(true);
        dragStartXRef.current = e.pageX;
        scrollLeftStartRef.current = tableContainerRef.current.scrollLeft;
        tableContainerRef.current.style.cursor = 'grabbing';
        tableContainerRef.current.style.userSelect = 'none';
      }
    }, 400);
  };

  const handleTableMouseMove = (e) => {
    if (!isDraggingRef.current || !tableContainerRef.current) return;

    e.preventDefault();
    const x = e.pageX;
    const walk = (dragStartXRef.current - x) * 1.5; // Scroll speed multiplier
    tableContainerRef.current.scrollLeft = scrollLeftStartRef.current + walk;
  };

  const handleTableMouseUp = () => {
    // Clear the long press timer if mouse is released before it fires
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    if (isDraggingRef.current && tableContainerRef.current) {
      isDraggingRef.current = false;
      setIsDragMode(false);
      tableContainerRef.current.style.cursor = '';
      tableContainerRef.current.style.userSelect = '';
    }
  };

  const handleTableMouseLeave = () => {
    handleTableMouseUp();
  };

  const getSortedOrders = (ordersToSort) => {
    if (!sortConfig.key) return ordersToSort;

    return [...ordersToSort].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Handle dates
      if (sortConfig.key.toLowerCase().includes('date')) {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        if (aDate < bDate) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aDate > bDate) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      }

      // Handle numbers
      if (!isNaN(aValue) && !isNaN(bValue)) {
        const aNum = parseFloat(aValue);
        const bNum = parseFloat(bValue);
        if (aNum < bNum) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aNum > bNum) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      }

      // Handle strings
      const aStr = aValue.toString().toLowerCase();
      const bStr = bValue.toString().toLowerCase();
      if (aStr < bStr) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

 const handleOrderClick = (orderNo) => {
    if (orderNo) {
      // Open in new tab instead of navigating
      window.open(`/work-order-profile/${orderNo}`, '_blank');
    } else {
      console.error('Attempted to navigate to an order with null or undefined orderNo');
    }
  };

  const [lookoutLookupPending, setLookoutLookupPending] = useState(new Set());

  // The main orders table has no appointment or production data on it, unlike
  // the Production Report, so bookmarking here needs to resolve it first via
  // enrichOrderForLookout (barcode/room/next-rooms always resolve; the
  // appointment is sometimes a best guess - see appointmentMatchType).
  // Removal doesn't need any of this.
  const handleToggleLookout = async (order, orderNo) => {
    if (isBookmarked(orderNo)) {
      toggleLookout({ orderNo });
      return;
    }

    setLookoutLookupPending(prev => new Set(prev).add(orderNo));

    const enrichment = await enrichOrderForLookout(orderNo, {
      customerName: order.CustomerFullName,
      dueDate: order.DueDate
    });

    setLookoutLookupPending(prev => {
      const next = new Set(prev);
      next.delete(orderNo);
      return next;
    });

    toggleLookout({
      orderNo,
      customerName: order.CustomerFullName,
      dueDate: order.DueDate,
      ...enrichment
    });
  };

  // Function to fetch orders with search term and server-side filters
  const fetchOrders = async (search = '', page = 1, append = false, serverFilters = {}) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
      }

      // Build URL with server-side filters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '100'
      });

      if (search) params.append('search', search);
      if (serverFilters.warehouse) params.append('warehouse', serverFilters.warehouse);
      if (serverFilters.orderStatus) params.append('status', serverFilters.orderStatus);
      if (serverFilters.orderType) params.append('stockFilter', serverFilters.orderType);

      const url = `${API_URL}/api/orders?${params.toString()}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || `HTTP error! status: ${response.status}`;
        } catch (e) {
          errorMessage = `HTTP error! status: ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Handle new response format
      const ordersArray = data.orders ? data.orders : (Array.isArray(data) ? data : []);
      
      if (append) {
        setOrders(prevOrders => [...prevOrders, ...ordersArray]);
      } else {
        setOrders(ordersArray);
        setCurrentPage(1); // Reset page when searching
      }
      
      // Update pagination info if available
      if (data.pagination) {
        setHasMore(data.pagination.hasMore);
        setTotalOrders(data.pagination.totalOrders);
        setCurrentPage(data.pagination.currentPage);
      } else {
        // Fallback for old response format
        setHasMore(false);
        setTotalOrders(ordersArray.length);
      }
      
      setLoading(false);
      setIsLoadingMore(false);
      setConnected(true);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(`Error fetching orders: ${err.message}`);
      setLoading(false);
      setIsLoadingMore(false);
      setConnected(false);
    }
  };

  const fetchWarrantyInfo = async (orderNo) => {
    if (!orderNo) return null;
    
    try {
      setLoadingWarranty(prev => new Set([...prev, orderNo]));
      
      console.log('Fetching warranty info for order:', orderNo);
      
      // ✅ Use the same endpoint as WorkOrderProfile - it will find the barcode automatically
      const response = await fetch(`${API_URL}/api/orders/${orderNo}/warranty`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      let warrantyInfo = {
        hasWarranty: false,
        status: 'No Warranty',
        daysRemaining: null,
        isActive: false,
        loaded: true
      };
      
      if (data.success && data.warranty) {
        const warranty = data.warranty;

        console.log('Raw warranty data from API:', warranty);

        // Support both snake_case (from DB) and camelCase naming
        const startDate = warranty.startDate || warranty.Wig_Warranty_Start_Date;
        const endDate = warranty.endDate || warranty.Wig_Warranty_Expires_Date;
        const wigBarCode = warranty.wigBarCode || warranty.Wig_Bar_Code;
        const resetCount = warranty.resetCount ?? warranty.WarrantyResetCount ?? 0;

        // Check if we have valid warranty data with dates
        if (endDate && startDate) {
          const today = new Date();
          const endDateObj = new Date(endDate);
          const daysRemaining = Math.max(0, Math.ceil((endDateObj - today) / (1000 * 60 * 60 * 24)));
          const isActive = daysRemaining > 0;

          warrantyInfo = {
            hasWarranty: true,
            startDate: startDate,
            endDate: endDate,
            status: isActive ? (warranty.status || warranty.Wig_Warranty_Status || 'Active') : 'Expired',
            daysRemaining,
            isActive: isActive,
            resetCount: resetCount,
            wigBarCode: wigBarCode,
            sourceOrderNo: warranty.sourceOrderNo || warranty.OrderNo,
            loaded: true
          };

          console.log(`✅ Warranty loaded for order ${orderNo} - ${isActive ? 'Active' : 'Expired'} (Days: ${daysRemaining}, End: ${endDate})`);
        } else {
          // No warranty data found via API
          warrantyInfo = {
            hasWarranty: false,
            status: 'No Warranty',
            daysRemaining: null,
            isActive: false,
            resetCount: 0,
            loaded: true,
            message: data.message || 'No warranty found for this order'
          };
          
          console.log('ℹ️ No warranty found for order:', orderNo);
        }
      } else {
        // API returned no warranty data
        warrantyInfo = {
          hasWarranty: false,
          status: 'No Warranty',
          daysRemaining: null,
          isActive: false,
          resetCount: 0,
          loaded: true,
          message: data.message || 'No warranty data available'
        };
      }
      
      setWarrantyData(prev => ({
        ...prev,
        [orderNo]: warrantyInfo
      }));
      
      return warrantyInfo;
    } catch (error) {
      console.error('❌ Error fetching warranty info:', error);
      
      // On error, set no warranty state
      setWarrantyData(prev => ({
        ...prev,
        [orderNo]: {
          hasWarranty: false,
          status: 'Error',
          daysRemaining: null,
          isActive: false,
          resetCount: 0,
          loaded: true,
          error: true,
          errorMessage: error.message
        }
      }));
      return null;
    } finally {
      setLoadingWarranty(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderNo);
        return newSet;
      });
    }
  };




  // Function to update OrderWarehouse field
  const updateOrderWarehouse = async (orderNo, newWarehouse) => {
    try {
      console.log(`Updating warehouse for order ${orderNo} to:`, newWarehouse);

      const response = await fetch(`${API_URL}/api/orders/${orderNo}/warehouse`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ warehouse: newWarehouse }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update warehouse: ${response.statusText}`);
      }

      const data = await response.json();

      // Update the local state to reflect the change
      setOrders(prevOrders =>
        prevOrders.map(order =>
          (order.orderno || order.OrderNo) === orderNo
            ? { ...order, OrderWarehouse: newWarehouse }
            : order
        )
      );

      console.log('✅ Warehouse updated successfully');
      return data;
    } catch (error) {
      console.error('❌ Error updating warehouse:', error);
      throw error;
    }
  };


  // Track the last searched term to avoid duplicate searches
  const lastSearchedTermRef = useRef('');

  // Execute search function - called on blur, Enter, or clear
  const executeSearch = useCallback((term) => {
    // Avoid duplicate searches for the same term
    if (term === lastSearchedTermRef.current) return;
    lastSearchedTermRef.current = term;

    const serverFilters = {
      warehouse: filters.warehouse,
      orderStatus: filters.orderStatus,
      orderType: filters.orderType
    };

    setCurrentPage(1);
    fetchOrders(term, 1, false, serverFilters);
  }, [filters.warehouse, filters.orderStatus, filters.orderType]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Search on blur (clicking outside the input)
  const handleSearchBlur = () => {
    executeSearch(searchTerm);
  };

  // Search on Enter key press
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch(searchTerm);
    }
  };

  // Clear search and fetch all results
  const handleClearSearch = () => {
    setSearchTerm('');
    lastSearchedTermRef.current = ''; // Reset so empty search will execute
    executeSearch('');
  };

  // Filter handlers
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      warehouse: '',
      wigType: '',
      enteredBy: '',
      orderStatus: '',
      orderType: ''
    });
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== '');
  };

  // Apply client-side filters only (warehouse, orderStatus, orderType are now server-side)
  const applyFilters = (ordersToFilter) => {
    let filtered = [...ordersToFilter];

    // NOTE: warehouse, orderStatus, and orderType are now filtered server-side
    // Only apply client-side filters that aren't supported by the server

    // Wig Type filter (Order Type) - client-side only
    if (filters.wigType) {
      filtered = filtered.filter(order => {
        const orderType = order.orderwigtype || order.OrderWigType || order.OrderType || order.orderType || '';
        if (filters.wigType === 'Send Out') {
          return orderType.toLowerCase().includes('send out');
        }
        return orderType.toLowerCase() === filters.wigType.toLowerCase();
      });
    }

    // Entered By filter - client-side only
    if (filters.enteredBy) {
      filtered = filtered.filter(order =>
        order.OrderCreatedBy?.toLowerCase().includes(filters.enteredBy.toLowerCase()) ||
        order.EnteredBy?.toLowerCase().includes(filters.enteredBy.toLowerCase())
      );
    }

    return filtered;
  };

  // Get unique values for filter dropdowns
  const getUniqueFilterValues = (field) => {
    const values = new Set();
    orders.forEach(order => {
      const value = order[field];
      if (value && value !== '') {
        values.add(value);
      }
    });
    return Array.from(values).sort();
  };
  
  const SimpleBalanceDisplay = ({ orderNo }) => {
    const [userRequestedLoad, setUserRequestedLoad] = useState(false);
    
    const { balanceDue, loading, error } = useOrderBalance(orderNo, userRequestedLoad);
  
    if (!userRequestedLoad) {
      return (
        <div className="summary-item balance-summary">
          <i className="fas fa-dollar-sign summary-icon"></i>
          <div className="summary-content">
            {/* <span className="summary-label">Balance Due:</span> */}
            <span 
              className="summary-value balance-badge clickable"
              onClick={() => setUserRequestedLoad(true)}
              title="Click to load balance"
            >
              <i className="fas fa-lock"></i> Click to view
            </span>
          </div>
        </div>
      );
    }
  
    if (loading) {
      return (
        <div className="summary-item balance-summary">
          <i className="fas fa-dollar-sign summary-icon"></i>
          <div className="summary-content">
            <span className="summary-label">Balance Due:</span>
            <span className="summary-value loading">
              <i className="fas fa-spinner fa-spin"></i> Loading...
            </span>
          </div>
        </div>
      );
    }
  
    if (error) {
      return (
        <div className="summary-item balance-summary">
          <i className="fas fa-dollar-sign summary-icon"></i>
          <div className="summary-content">
            <span className="summary-label">Balance Due:</span>
            <span className="summary-value error">
              <i className="fas fa-exclamation-triangle"></i> Unavailable
            </span>
          </div>
        </div>
      );
    }
  
    return (
      <div className="summary-item balance-summary">
        <i className="fas fa-dollar-sign summary-icon"></i>
        <div className="summary-content">
          <span className="summary-label">Balance Due:</span>
          <span className={`summary-value ${balanceDue > 0 ? 'amount-due' : 'amount-paid'}`}>
            {formatCurrency(Math.abs(balanceDue))}
            {balanceDue < 0 && <small>(Credit)</small>}
            {balanceDue === 0 && <span ></span>}
          </span>
        </div>
      </div>
    );
  };

  // Simple Warranty Display - single line like before
  const SimpleWarrantyDisplay = ({ orderNo }) => {
    const warrantyInfo = warrantyData[orderNo];
    const isLoading = loadingWarranty.has(orderNo);

    // Auto-fetch warranty data if not already loaded
    React.useEffect(() => {
      if (!warrantyInfo && !isLoading) {
        fetchWarrantyInfo(orderNo);
      }
    }, [orderNo, warrantyInfo, isLoading]);

    if (isLoading) return <div className="summary-item loading"><i className="fas fa-spinner fa-spin"></i> Loading...</div>;

    // Use isActive instead of hasWarranty to check if warranty is not expired
    const isUnderWarranty = warrantyInfo?.hasWarranty && warrantyInfo?.isActive;

    return (
      <div className="summary-item warranty-summary">
        <i className="fas fa-shield-alt summary-icon"></i>
        <span className={`warranty-badge-simple ${isUnderWarranty ? 'warranty-active' : 'warranty-none'}`}>
          {isUnderWarranty ? 'Under Warranty' : 'No Warranty'}
        </span>
      </div>
    );
  };


const handleRowExpand = (orderNo) => {
  const newExpandedRows = new Set(expandedRows);
  
  if (expandedRows.has(orderNo)) {
    newExpandedRows.delete(orderNo);
  } else {
    newExpandedRows.add(orderNo);
    
    if (!warrantyData[orderNo]) {
      fetchWarrantyInfo(orderNo); 
    }
  }
  
  setExpandedRows(newExpandedRows);
};


  // Function to show more results locally
  const loadMoreOrders = () => {
    if (hasMore && !isLoadingMore && !loading) {
      const serverFilters = {
        warehouse: filters.warehouse,
        orderStatus: filters.orderStatus,
        orderType: filters.orderType
      };
      fetchOrders(searchTerm, currentPage + 1, true, serverFilters);
    }
  };

  // Function to retry after error
  const retryFetch = () => {
    setError(null);
    const serverFilters = {
      warehouse: filters.warehouse,
      orderStatus: filters.orderStatus,
      orderType: filters.orderType
    };
    fetchOrders(searchTerm, 1, false, serverFilters);
  };

  // Safe access to orders array
  const safeOrders = Array.isArray(orders) ? orders : [];

  // Memoize column names to avoid recalculating on every render
  // Must be called before any early returns (React hooks rule)
  const columns = useMemo(() => {
    const allColumns = safeOrders.length > 0 && typeof safeOrders[0] === 'object'
      ? Object.keys(safeOrders[0])
      : [];
    return [
      ...new Set([
        ...allColumns.filter(col => col !== 'DueDate')
      ])
    ].filter(column =>
      column.toLowerCase() !== 'serial_num' &&
      column.toLowerCase() !== 'serialnum' &&
      !column.toLowerCase().includes('serial')
    ).concat(['DueDate']);
  }, [safeOrders]);

  // Memoize filtered and sorted orders to avoid recalculating on every render
  // Initial sort is handled by backend (ID DESC, OrderDate DESC), user can override by clicking headers
  const { displayedOrders, filteredOrders } = useMemo(() => {
    const filtered = applyFilters(safeOrders);
    const sorted = getSortedOrders(filtered);
    return {
      filteredOrders: filtered,
      displayedOrders: sorted.slice(0, displayLimit)
    };
  }, [safeOrders, filters, sortConfig, displayLimit]);

  if (loading && safeOrders.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Error Loading Data</h3>
        <p>{error}</p>
        <p>This might be due to a query timeout. Try using a more specific search term or loading fewer records.</p>
        <button className="retry-btn" onClick={retryFetch}>Try Again</button>
      </div>
    );
  }

  // Only show "no data" screen if there's truly no data (no search/filter active)
  if (safeOrders.length === 0 && !searchTerm && !orderNoSearch && !hasActiveFilters()) {
    return (
      <div className="no-data-container">
        <h3>No Orders Found</h3>
        <p>Database connection successful, but no orders were found.</p>
      </div>
    );
  }



  return (
    <div className="orders-container">

      {/* <div className="view-controls">
        <h2>Orders List</h2>
      </div> */}

      {/* DATE RANGE REPORT COMPONENT */}
      <DateRangeReport />

 

        {/* SEARCH & ROOM LAUNCH */}
        <div className="search-container">
          <div className="search-and-launch-row">
            <div className="search-group">
              <input
                type="text"
                placeholder="Search by barcode, order #, or customer name... (Click outside input or press Enter to search)"
                value={searchTerm}
                onChange={handleSearchChange}
                onBlur={handleSearchBlur}
                onKeyDown={handleSearchKeyDown}
                className="search-input"
              />
              <button
                className="search-btn-icon"
                onClick={() => executeSearch(searchTerm)}
                title="Search"
              >
                <i className="fas fa-search"></i>
              </button>
              {searchTerm && (
                <button
                  className="clear-search-btn"
                  onClick={handleClearSearch}
                >
                  Clear
                </button>
              )}
            </div>

            <div className="room-launch-wrapper">
              <RoomLaunchSidebar />
            </div>

            <button
              className="past-due-tasks-nav-btn"
              onClick={() => navigate('/past-due-tasks')}
              title="View past due tasks in Machine Room (1002) & Hand Sew Room (3001)"
            >
              <i className="fas fa-clock"></i>
              <span>Past Due Tasks</span>
            </button>
          </div>

          {orderSearchError && (
            <div className="search-error">
              <i className="fas fa-exclamation-triangle"></i>
              {orderSearchError}
            </div>
          )}
        </div>      
      {loading && safeOrders.length > 0 && (
        <div className="search-loading">
          <div className="loading-spinner-small"></div>
          <span>Updating results...</span>
        </div>
      )}
             {/* FILTERS */}
        <div className="filters-container">
          <div className="filters-row-ol">
            
            {/* Order Type Filter (Stock vs Client) */}
            <div className="filter-bubble">
              <select
                value={filters.orderType}
                onChange={(e) => handleFilterChange('orderType', e.target.value)}
                className="filter-select"
              >
                <option value="">Stock & Client</option>
                <option value="stock">Stock Orders</option>
                <option value="client">Client Orders</option>
              </select>
            </div>
           
            {/* Order Type Filter */}
            <div className="filter-bubble">
              <select
                value={filters.wigType}
                onChange={(e) => handleFilterChange('wigType', e.target.value)}
                className="filter-select"
              >
                <option value="">All Order Types</option>
                <option value="New Wig">New Wig</option>
                <option value="New Sale Wig">New Sale Wig</option>
                <option value="Aidel New Wig">Aidel New Wig</option>
                <option value="Wash & Set">Wash & Set</option>
                <option value="Wig Repair">Wig Repair</option>
                <option value="Aidel Wig Repair">Aidel Wig Repair</option>
                <option value="Consultation">Consultation</option>
                <option value="Accessory">Accessory</option>
                <option value="Recut">Recut</option>
                <option value="Send Out">Send Out (All Types)</option>
              </select>
            </div>

            {/* Entered By Filter */}
            <div className="filter-bubble">
              <select
                value={filters.enteredBy}
                onChange={(e) => handleFilterChange('enteredBy', e.target.value)}
                className="filter-select"
              >
                <option value="">All Users</option>
                <option value="Amberrose">Amberrose</option>
                <option value="Ashley">Ashley</option>
                <option value="Ayala">Ayala</option>
                <option value="ChenW">ChenW</option>
                <option value="EttiW">EttiW</option>
                <option value="Denissi">Denissi</option>
                <option value="DidiP">DidiP</option>
                <option value="GKirsh">GKirsh</option>
                <option value="JennyC">JennyC</option>
                <option value="LucieV">LucieV</option>
                <option value="MayaH">MayaH</option>
                <option value="Office">Office</option>
                <option value="RachelG">RachelG</option>
                <option value="Yael">Yael</option>
              </select>
            </div>

            {/* Order Status Filter */}
            <div className="filter-bubble">
              <select
                value={filters.orderStatus}
                onChange={(e) => handleFilterChange('orderStatus', e.target.value)}
                className="filter-select"
              >
                <option value="">All Statuses</option>
                {getUniqueFilterValues('OrderStatus').map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
 {/* Warehouse Filter */}
            <div className="filter-bubble">
              <select
                value={filters.warehouse}
                onChange={(e) => handleFilterChange('warehouse', e.target.value)}
                className="filter-select"
              >
                <option value="">All Warehouses</option>
                <option value="Pomona">Pomona</option>
                <option value="Lakewood">Lakewood</option>
                <option value="Israel">Israel</option>
                <option value="China">China</option>
                <option value="China (Nely)">China (Nely)</option>  
              </select>
            </div>


            {/* Clear Filters Button */}
            {hasActiveFilters() && (
              <button
                className="ol-clear-filters-btn"
                onClick={clearAllFilters}
                title="Clear all filters"
              >
                <i className="fas fa-times"></i> Clear Filters
              </button>
            )}
          </div>
        </div>
      
        
      <div
        className={`table-container ${isDragMode ? 'drag-mode' : ''}`}
        ref={tableContainerRef}
        onMouseDown={handleTableMouseDown}
        onMouseMove={handleTableMouseMove}
        onMouseUp={handleTableMouseUp}
        onMouseLeave={handleTableMouseLeave}
      >
        {columns.length > 0 ? (
          <table className="orders-table">
            <thead>
              <tr>
                {columns.filter(column => column !== 'id').map(column => (
                  <th
                    key={column}
                    onClick={() => handleSort(column)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    className={`col-${column} ${sortConfig.key === column ? `sorted-${sortConfig.direction}` : ''}`}
                  >
                    {column}
                    {sortConfig.key === column && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedOrders.length === 0 ? (
                <tr>
                  <td colSpan={columns.filter(c => c !== 'id').length} style={{
                    textAlign: 'center',
                    padding: '3rem 1rem',
                    color: 'var(--text-secondary)',
                    fontSize: '1.1rem'
                  }}>
                    <i className="fas fa-search" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block', opacity: 0.5 }}></i>
                    <div style={{ marginBottom: '0.5rem', fontWeight: '600' }}>
                      No results found
                    </div>
                    <div style={{ fontSize: '0.95rem' }}>
                      {searchTerm && `No orders match "${searchTerm}"`}
                      {!searchTerm && hasActiveFilters() && 'No orders match the selected filters'}
                      {searchTerm && hasActiveFilters() && ' with the current filters'}
                    </div>
                    <div style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.7 }}>
                      Try adjusting your search or filter criteria
                    </div>
                  </td>
                </tr>
              ) : (
                displayedOrders.map((order) => {
                const orderNo = order.orderno || order.OrderNo;
                const isExpanded = expandedRows.has(orderNo);
                const tasks = roomTasks[orderNo] || [];
                const isLoadingTasks = loadingTasks.has(orderNo);
                // const sortedTasks = sortTasks(tasks);
                const isOrderDueSoon = isDueSoon(order.DueDate, order.OrderStatus, order.CustomerFullName);

                return (
                  <React.Fragment key={orderNo}>
                  <tr
                    className={`order-row ${isExpanded ? 'expanded' : ''} ${isOrderDueSoon ? 'due-soon' : ''}`}
                    onClick={() => handleRowExpand(orderNo)}
                    style={{
                    cursor: 'pointer',
                    backgroundColor: isOrderDueSoon ? 'rgba(239, 68, 68, 0.15)' : undefined,
                    transition: 'background-color 0.2s ease'
                    }}
                  >
                    {columns.filter(column => column !== 'id').map((column, colIndex) => (
                    <td key={`${orderNo}-${column}`} data-label={column} className={`col-${column}`}>
                      {colIndex === 0 && (
                      <span className={`expand-icon ${isExpanded ? 'rotated' : ''}`}>
                        ▶
                      </span>
                      )}

                      {colIndex === 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleLookout(order, orderNo);
                        }}
                        disabled={lookoutLookupPending.has(orderNo)}
                        title={isBookmarked(orderNo) ? 'Remove from Lookout List' : 'Add to Lookout List'}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: isBookmarked(orderNo) ? '#d4af37' : '#999',
                          cursor: lookoutLookupPending.has(orderNo) ? 'wait' : 'pointer',
                          padding: '2px 6px',
                          fontSize: '13px',
                          opacity: isBookmarked(orderNo) ? 1 : 0.6,
                          transition: 'opacity 0.2s, color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = isBookmarked(orderNo) ? 1 : 0.6}
                      >
                        {lookoutLookupPending.has(orderNo) ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className={`${isBookmarked(orderNo) ? 'fas' : 'far'} fa-bookmark`}></i>
                        )}
                      </button>
                      )}


                      {column === "OrderWarehouse" ? (
                      <EditableCell
                        value={order[column] || ''}
                        onSave={(newValue) => updateOrderWarehouse(orderNo, newValue)}
                        placeholder="Click to select warehouse"
                        type="select"
                        options={[
                        { value: '', label: 'Select warehouse...' },
                        { value: 'Pomona', label: 'Pomona' },
                        { value: 'Lakewood', label: 'Lakewood' },
                        { value: 'Israel', label: 'Israel' },
                        { value: 'China', label: 'China' }
                        ]}
                      />
                    )
                    : order[column] !== null && order[column] !== undefined
                    ? (column === "DueDate"
                      ? formatShortDate(order[column])
                      : column === "OrderDate"
                      ? formatDateTimeForUser(order[column], currentUser)
                      : column === "OrderPhone"
                      ? formatPhone(order[column])
                      : column === "CustomerFullName"
                      ? (() => {
                          const isRestrictedUser = restrictedUserList.includes(currentUser);
                          return isRestrictedUser ? (

                            <span>{order.CustomerFullName || ''}</span>
                          ) : (
                          <Link
                            to={`/customers/${order.id}`}
                            className="text-primary"
                            style={{ cursor: 'pointer' }}
                          >
                            {order.CustomerFullName || ''}
                          </Link>
                        )
                        })()
                        : column === "orderno" || column === "OrderNo"
                        ? (
                            <a
                            href={`/work-order-profile/${order[column]}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (order[column]) {
                              handleOrderClick(order[column]);
                              }
                            }}
                            className="text-primary"
                            style={{ cursor: 'pointer' }}
                            target="_blank"
                            rel="noopener noreferrer"
                            >
                            {order[column].toString()}
                            </a>
                          )
                        : order[column].toString())                                
                      : ''
                      }
                    </td>
                    ))}
                  </tr>
                  
                {isExpanded && (
            <tr className="expanded-content">
              <td colSpan={columns.filter(c => c !== 'id').length}>
                
              
                {isLoadingTasks ? (
                  <div className="loading-tasks-fresh">
                  <div className="loading-spinner-large"></div>
                  <span>Loading room tasks...</span>
                  </div>
                ) : (
                  <ProductionSchedule
                  orderNo={orderNo}
                  />
                )}
                  {/* Warrenty, Balance, and Print Buttons */}
              <div className="button-group-warranty-balance">
                
                <SimpleWarrantyDisplay orderNo={orderNo} />
                <SimpleBalanceDisplay orderNo={orderNo} />
                <button 
                className="btn btn-primary rounded-pill shadow-sm mr-2"
                onClick={() => worksheetHandler(orderNo)}
                title="Print Worksheet"
                >
                <i className="fas fa-print mr-1"></i> Print Worksheet
                </button>

                <button 
                className="btn btn-success rounded-pill shadow-sm"
                onClick={() => receiptHandler(orderNo)}
                title="Print Receipt"
                style={{
                  background: 'linear-gradient(135deg, #d4af37 0%, #ffd700 100%)',
                  border: 'none',
                  color: '#2c2c2c',
                  fontWeight: '600'
                }}
                >
                <i className="fas fa-receipt mr-1"></i> Print Receipt
                </button>

                <EmailInvoiceButton orderNo={orderNo} />
          
              </div>
              
                  </td>
                  
                  </tr>
                  
                )}

                  </React.Fragment>
                );
              })
              )}

            </tbody>
            
          </table>
        ) : (
          <div className="no-data-container">
            <p>Unable to display orders. The data format may be incorrect.</p>
          </div>
        )}

      </div>
      
      
      {/* Only show Load More if there are more orders to display */}
      {hasMore && (
        <div className="load-more-container">
          <button
            className="load-more-btn"
            onClick={loadMoreOrders}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading...' : 'Load More Orders'}
          </button>
        </div>
      )}
      
      
      <div className="results-count">
        Showing {filteredOrders.length} of {totalOrders} orders
        {searchTerm && <span> (filtered by search: "{searchTerm}")</span>}
        {hasActiveFilters() && <span> (with active filters)</span>}
      </div>

      {showReceipt && getCurrentOrderData() && (
        <OrderReceipt
          orderDetails={getCurrentOrderData().orderDetails}
          customerInfo={getCurrentOrderData().customerInfo}
          wigDetails={getCurrentOrderData().wigDetails}
          orderItems={[]} // Keep empty - receipt uses lineItemsData instead
          payments={[]}   // Keep empty - receipt uses paymentsData instead
          totals={{
            subtotal: 0,
            tax: 0,
            shipping: 0,
            deposit: 0,
            discount: 0,
            taxRate: 8.375
          }}
          paymentSummary={{}}
          isVisible={showReceipt}
          onClose={handleCloseReceipt}
          lineItemsData={printOrderFinancialData.lineItems}
          paymentsData={printOrderFinancialData.payments}
        />
      )}

      {showWorksheet && getCurrentOrderData() && (
        <OrderWorksheet
          orderDetails={worksheetSpecificData || getCurrentOrderData().orderDetails}
          customerInfo={getCurrentOrderData().customerInfo}
          wigDetails={worksheetSpecificData || getCurrentOrderData().wigDetails}
          roomTasks={worksheetSpecificData?.roomTasks || getCurrentOrderData().roomTasks}
          isVisible={showWorksheet}
          onClose={handleCloseWorksheet}
        />
      )}

    </div>
  );
};

export default OrdersList;